import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Button, Stack } from '@mui/material';
import { GridCellParams, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useOperatorsMap, useStockLocationsMap, useSuppliersMap } from '../../../admin/hooks';
import {
    formatDateTimeString,
    IBatch,
    IParams,
    IProduct,
    Loading,
    SortOrder,
    Table,
    useQueryParams,
    Widget,
} from '../../../shared';
import { useStockMovementsList } from '../../../stock/hooks';
import { useSaveShipment, useShipment, useShipmentStockMovements } from '../../hooks';

export const ShipmentContentPage: FC = () => {
    const { t } = useTranslation();
    const { id } = useParams<keyof IParams>() as IParams;
    const navigate = useNavigate();
    const { sort, setSort } = useQueryParams({
        defaultSort: [{ field: 'createdDate', sort: 'desc' }],
    });
    const [selectedStockMovements, setSelectedStockMovements] = useState<string[]>([]);

    const suppliers = useSuppliersMap();
    const operators = useOperatorsMap();
    const stockLocations = useStockLocationsMap();

    const { mutateAsync: saveShipment, isPending: isPendingSave } = useSaveShipment();
    const { data: shipment, isPending: shipmentLoading } = useShipment(id);
    const { data: shipmentStockMovements, isPending: shipmentStockMovementsLoading } = useShipmentStockMovements(id);
    const { data: stockMovements, isPending: stockMovementsLoading } = useStockMovementsList(
        {
            page: 1,
            pageSize: 10000,
            sortBy: sort[0].field,
            sortOrder: sort[0].sort?.toUpperCase() as SortOrder,
            organisation: shipment?.receiver.id,
            shipment: shipment?.id,
            readyForShipment: true,
            includeRelations: false,
        },
        { enabled: Boolean(shipment?.receiver) },
    );

    const onNext = async () => {
        if (shipment) {
            await saveShipment({
                id,
                item: {
                    ...shipment,
                    receiverId: shipment.receiver.id,
                    stockMovements: selectedStockMovements,
                },
            });
            navigate(`/planning/shipments/${shipment.id}/attachments`);
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'createdDate',
            headerName: t('createdDate'),
            valueFormatter: (value: string) => formatDateTimeString(value),
            flex: 1,
        },
        {
            field: 'type',
            headerName: t('type'),
            valueFormatter: (value: string) => t(value),
            flex: 1,
        },
        {
            field: 'operatorId',
            headerName: t('operator'),
            valueFormatter: (value: string) => operators[value],
            flex: 1,
        },
        {
            field: 'supplierId',
            headerName: t('supplier'),
            flex: 1,
            renderCell: ({ row }: GridCellParams) =>
                row.batch ? suppliers[row.batch.supplierId] : suppliers[row.supplierId],
        },
        {
            field: 'batch',
            headerName: t('batch'),
            valueFormatter: (value: IBatch) => value?.referenceNumber,
            flex: 1,
            minWidth: 150,
            sortable: false,
        },
        {
            field: 'stockLocationId',
            headerName: t('stockLocation'),
            valueFormatter: (value: string) => stockLocations[value],
            flex: 1,
        },
        {
            field: 'product',
            headerName: t('product'),
            valueFormatter: (value: IProduct) => value?.name,
            flex: 1,
        },
        { field: 'amount', headerName: t('amount'), minWidth: 150, flex: 1 },
    ];

    useEffect(() => {
        if (shipmentStockMovements) {
            setSelectedStockMovements(shipmentStockMovements.map(({ id }) => id));
        }
    }, [shipmentStockMovements]);

    const onSelectionChange = (ids: GridRowSelectionModel) => {
        setSelectedStockMovements(ids as string[]);
    };

    return (
        <Widget
            hasTable
            footer={
                <Stack direction="row" justifyContent="space-between">
                    <Button startIcon={<ChevronLeft />} onClick={() => navigate(`/planning/shipments/${id}`)}>
                        {t('previous')}
                    </Button>
                    <Button
                        endIcon={<ChevronRight />}
                        variant="contained"
                        color="primary"
                        onClick={onNext}
                        disabled={isPendingSave}
                    >
                        {t('next')}
                    </Button>
                </Stack>
            }
        >
            {stockMovementsLoading || shipmentLoading || shipmentStockMovementsLoading ? (
                <Loading />
            ) : (
                <>
                    {stockMovements?.data && (
                        <Table
                            checkboxSelection
                            rowSelectionModel={selectedStockMovements}
                            onRowSelectionModelChange={onSelectionChange}
                            rows={stockMovements.data || []}
                            columns={columns}
                            onSortModelChange={setSort}
                            sortModel={sort}
                            paginationMode="client"
                            sortingMode="client"
                            pageSizeOptions={[100]}
                            hideFooter={stockMovements.data.length <= 100}
                        />
                    )}
                </>
            )}
        </Widget>
    );
};
